/**
 * Which rows of a story list that is already on screen just "popped in": a ranking arrived while the reader
 * was reading and put a story they had not been shown above one they had, shifting the list. BetaStoryList
 * marks those rows with a brief highlight (`.beta-story.is-arrived`) so the shift has a visible cause.
 *
 * Deliberately not an arrival:
 * - the list's first stories (nothing was on screen to shift);
 * - stories added below everything already shown (a later page, the rest of a build): nothing moved;
 * - a story the list showed under another face: a story is known by its face, its alternatives and its topic,
 *   so a new face for a story already shown (For You re-ranking a delivered story) is a reorder, not news;
 * - a ranking that replaces most of the list (more new stories than already-shown ones): highlighting nearly
 *   every row marks nothing.
 */
import type { FeedStory } from '../repositories/readerStory';

type StoryNames = Pick<FeedStory, 'id' | 'alternatives' | 'topic_id'>;

/** Every name a story goes by in a list: its face, its alternatives and its topic. */
export function storyNames(story: StoryNames): string[] {
  return [
    `article:${story.id}`, ...(story.alternatives ?? []).map(alternative => `article:${alternative.id}`),
    ...(story.topic_id ? [`topic:${story.topic_id}`] : []),
  ];
}

/**
 * The ids of *stories* (in list order) that arrived above a story the list had already shown, given *shown*:
 * every name (`storyNames`) of every story this list has shown so far.
 */
export function arrivedStoryIds(shown: ReadonlySet<string>, stories: readonly StoryNames[]): string[] {
  const known = stories.map(story => storyNames(story).some(name => shown.has(name)));
  const lastKnown = known.lastIndexOf(true);
  const arrived = stories.filter((_, index) => index < lastKnown && !known[index]).map(story => story.id);
  const stayed = known.filter(Boolean).length;
  return arrived.length > stayed ? [] : arrived;
}
